import {
  Button,
  Card,
  DisplayText,
  Page,
} from "@rentiohq/web-shared/dist/components";
import React from "react";
import { Navigate, useParams } from "react-router-dom";
import {
  ACADEMY_BASE_PATH,
  ACADEMY_CHAPTERS,
  ACADEMY_SECTION_PARAM,
} from "../../RentmatchAcademy.constants";
import {
  useAcademyQuizAnswers,
  useAcademySearchQuery,
} from "../../RentmatchAcademy.hooks";
import {
  getAcademyText,
  getChapterById,
  getChapterKey,
  getChapterNumber,
  getChapterScore,
  getQuizAnswerKey,
  getSectionAnchor,
  getSectionKey,
  getUiKey,
} from "../../RentmatchAcademy.utils";
import { AcademyFaq } from "../AcademyFaq";
import { AcademyHighlight } from "../AcademyHighlight";
import { AcademyMedia } from "../AcademyMedia";
import { AcademyQuiz } from "../AcademyQuiz";
import { AcademyText } from "../AcademyText";
import * as S from "./AcademyChapter.styles";

export const AcademyChapter = () => {
  const { chapterId } = useParams<{ chapterId: string }>();
  const chapter = getChapterById(chapterId);
  const { answers, setAnswer } = useAcademyQuizAnswers();
  const { query, searchParams } = useAcademySearchQuery();
  // Set when the broker came from a search result: that section opens in
  // view, with the search marked in it.
  const targetSectionId = searchParams.get(ACADEMY_SECTION_PARAM) ?? undefined;

  // Lifecycle
  React.useEffect(() => {
    const target = targetSectionId
      ? document.getElementById(getSectionAnchor(chapterId, targetSectionId))
      : null;
    if (target) {
      target.scrollIntoView({ block: "start" });

      return;
    }
    // The previous and next buttons keep the page mounted, so a new chapter
    // would otherwise open scrolled to where the last one ended.
    window.scrollTo(0, 0);
  }, [chapterId, targetSectionId]);

  if (!chapter) {
    return <Navigate to={ACADEMY_BASE_PATH} replace={true} />;
  }

  const chapterNumber = getChapterNumber(chapter);
  const previousChapter = ACADEMY_CHAPTERS[chapterNumber - 2];
  const nextChapter = ACADEMY_CHAPTERS[chapterNumber];

  const score = getChapterScore(chapter, answers);

  // Render
  const renderSections = () =>
    chapter.sections.map(section => {
      const title = getAcademyText(
        getSectionKey(chapter.id, section.id, "title"),
      );
      const isTarget = section.id === targetSectionId;
      const sectionQuery = isTarget ? query : undefined;

      return (
        // Section ids repeat across chapters ("invite"), and the page stays
        // mounted between chapters, so the key carries the chapter too: an
        // answered question must not carry over to the next chapter.
        <S.Anchor
          key={`${chapter.id}-${section.id}`}
          id={getSectionAnchor(chapter.id, section.id)}
          isTarget={isTarget}
        >
          <Card
            // An element heading skips the card's own title style, so the
            // highlighted title carries it itself.
            heading={
              <DisplayText size="medium" space="none">
                <AcademyHighlight text={title} query={sectionQuery} />
              </DisplayText>
            }
          >
            <S.Section hasMedia={!!section.media}>
              <AcademyText
                text={getAcademyText(
                  getSectionKey(chapter.id, section.id, "body"),
                )}
                query={sectionQuery}
              />
              {section.media && (
                <AcademyMedia mediaId={section.media} alt={title} />
              )}
            </S.Section>
            {section.quizAnswer && (
              <AcademyQuiz
                chapterId={chapter.id}
                sectionId={section.id}
                answer={section.quizAnswer}
                score={score}
                selected={answers[getQuizAnswerKey(chapter.id, section.id)]}
                onSelect={option =>
                  setAnswer(getQuizAnswerKey(chapter.id, section.id), option)
                }
              />
            )}
          </Card>
        </S.Anchor>
      );
    });

  const renderNavigation = () => (
    <S.Navigation>
      {previousChapter ? (
        <Button
          appearance="outline"
          url={`${ACADEMY_BASE_PATH}/${previousChapter.id}`}
        >
          {getAcademyText(getUiKey("chapter.previous"))}
        </Button>
      ) : (
        <span />
      )}
      {nextChapter && (
        <Button
          appearance="primary"
          url={`${ACADEMY_BASE_PATH}/${nextChapter.id}`}
        >
          {getAcademyText(getUiKey("chapter.next"))}
        </Button>
      )}
    </S.Navigation>
  );

  return (
    <Page
      title={getAcademyText(getChapterKey(chapter.id, "title"))}
      subtitle={getAcademyText(getUiKey("chapter.label"), {
        number: `${chapterNumber}`,
      })}
      breadcrumbs={{
        to: ACADEMY_BASE_PATH,
        content: getAcademyText("system.back_to_overview"),
      }}
    >
      <S.Intro>{getAcademyText(getChapterKey(chapter.id, "intro"))}</S.Intro>
      {chapter.isQuestionList ? (
        <AcademyFaq
          // Remounts when a search result points at another question, so
          // that one opens.
          key={`${chapter.id}-${targetSectionId}`}
          chapterId={chapter.id}
          sections={chapter.sections}
          openSectionId={targetSectionId}
          query={query}
        />
      ) : (
        renderSections()
      )}
      {renderNavigation()}
    </Page>
  );
};
